/** /t/c/<courseId>/items — the item bank: filter, edit, duplicate, archive, add. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, download } from "../api/client";

import { approveDraftItems, archiveItem, awaitGeneration, createItem, duplicateItem, generateItems, getCourse, getHostInfo, getItemStats, awaitRewrite, getItem, importQti, isOfflineError, listRewrites, noServerHere, patchItem, proposeRewrite, qtiExportPath, recordEdit, reviewItem, searchItems, setItemReview, type Item, type ItemFilters, type ItemFlag, type ItemIn, type ItemStat, type Rewrite } from "../teacher/api";
import { TeacherChrome } from "../teacher/Chrome";
import { ItemEditor } from "../teacher/ItemEditor";
import { currentDraft, itemQueryKeys, rewriteErrorKey, rewriteRequest, rewriteState } from "../teacher/rewrites";
import { Button, DraftChip, DraftFrame, SaveNotice, useConfirm, useFileSave } from "../ui";

export function Items() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<ItemFilters>({});
  const [editing, setEditing] = useState<Item | null | "new">(null);
  const [gen, setGen] = useState({ lo_id: "", count: 3, type: "single", bloom: "" });
  const [genState, setGenState] = useState<{ busy: boolean; msg: string | null }>({ busy: false, msg: null });
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const items = useQuery({ queryKey: ["items", courseId, filters], queryFn: () => searchItems(courseId, filters) });
  const refresh = () => {
    for (const queryKey of itemQueryKeys(courseId)) void qc.invalidateQueries({ queryKey });
  };
  const archive = useMutation({ mutationFn: archiveItem, onSuccess: refresh });
  const duplicate = useMutation({ mutationFn: duplicateItem, onSuccess: refresh });
  // The approval gate (CLAUDE.md): nothing drafted reaches a student until a teacher says so.
  const [reviewMsg, setReviewMsg] = useState<string | null>(null);
  const setStatus = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: "draft" | "approved" }) => setItemReview(itemId, status),
    onSuccess: (it) => { setReviewMsg(it.review_status === "approved" ? t("items.approval.approvedOne") : t("items.approval.draftedOne")); refresh(); },
    onError: (e) => setReviewMsg(isOfflineError(e) ? t("editor.failed") : String(e)),
  });
  const approveDrafts = useMutation({
    mutationFn: () => approveDraftItems(courseId, filters.lo_id ?? null),
    onSuccess: (r) => { setReviewMsg(t("items.approval.approvedN", { count: r.approved })); refresh(); },
    onError: (e) => setReviewMsg(isOfflineError(e) ? t("editor.failed") : String(e)),
  });
  const stats = useQuery({ queryKey: ["item-stats", courseId], queryFn: () => getItemStats(courseId) });
  // With no server behind this computer the bank is fully editable by hand, but /ai is not there.
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false });
  const noAi = noServerHere(host.data);
  const review = useMutation({
    mutationFn: ({ itemId, action }: { itemId: string; action: "dismiss" | "reopen" }) => reviewItem(courseId, itemId, { action }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["item-stats", courseId] }),
  });
  // AI stem rewrites (STORY-045): not where no server is signed in, and not in the Teacher
  // Computer app at all, whose own curriculum service has no ai service behind it.
  const rewritesHere = !host.isPending && !noAi && !host.data?.local_content;
  const rewrites = useQuery({ queryKey: ["rewrites", courseId], queryFn: () => listRewrites(courseId), enabled: rewritesHere });
  const rewriteByItem = new Map((rewrites.data ?? []).map((r) => [r.item_id, r] as const));
  // a refusal that left no row behind (no objective, ai service unreachable), per item
  const [rewriteRefused, setRewriteRefused] = useState<Record<string, string>>({});
  const polling = useRef(new Set<string>());
  const putRewrite = (r: Rewrite) =>
    qc.setQueryData<Rewrite[]>(["rewrites", courseId], (old) => [...(old ?? []).filter((x) => x.item_id !== r.item_id), r]);
  const follow = async (r: Rewrite) => {
    if (polling.current.has(r.job_id)) return;
    polling.current.add(r.job_id);
    try {
      const done = await awaitRewrite(r.item_id, r.job_id);
      putRewrite(done);
      if (done.draft) void qc.invalidateQueries({ queryKey: ["items", courseId] });
    } catch (e) {
      putRewrite({ ...r, status: "failed", error: isOfflineError(e) ? "ai_unavailable" : String(e) });
    } finally {
      polling.current.delete(r.job_id);
    }
  };
  useEffect(() => {
    for (const r of rewrites.data ?? []) if (rewriteState(r) === "working") void follow(r);
  }, [rewrites.data]);
  const propose = async (s: ItemStat) => {
    setRewriteRefused((m) => {
      const next = { ...m };
      delete next[s.item_id];
      return next;
    });
    try {
      const r = await proposeRewrite(s.item_id, rewriteRequest(s));
      putRewrite(r);
      void follow(r);
    } catch (e) {
      const code = isOfflineError(e) ? "ai_unavailable" : e instanceof ApiError && e.code ? e.code : String(e);
      setRewriteRefused((m) => ({ ...m, [s.item_id]: code }));
    }
  };
  // the draft as saved now, not the copy the rewrite list was read with
  const openDraft = async (rw: Rewrite) => {
    try {
      const it = await currentDraft(rw, getItem, (e) => e instanceof ApiError && e.status === 404);
      if (it) setEditing(it);
      else refresh();
    } catch (e) {
      setReviewMsg(isOfflineError(e) ? t("editor.failed") : String(e));
    }
  };
  const failureText = (error: string | null | undefined) => t(rewriteErrorKey(error), { error: error ?? "" });
  const statById = new Map((stats.data?.items ?? []).map((s) => [s.item_id, s] as const));
  const queue = (stats.data?.items ?? []).filter((s) => s.flags.length > 0 && s.review === "open");
  const dismissed = (stats.data?.items ?? []).filter((s) => s.flags.length > 0 && s.review === "dismissed");
  const fmtP = (p: number | null) => (p == null ? t("items.stats.noD") : `${Math.round(p * 100)}%`);
  const fmtD = (d: number | null | undefined) => (d == null ? t("items.stats.noD") : d.toFixed(2));
  const flagText = (f: ItemFlag) => t(`items.stats.flags.${f.code}`, { p: fmtP(f.p ?? null), d: fmtD(f.discrimination), keys: (f.keys ?? []).join(", ") });
  const statLine = (s: ItemStat) => t("items.stats.line", { n: s.n, p: fmtP(s.p), d: fmtD(s.discrimination) });
  const [qtiMsg, setQtiMsg] = useState<string | null>(null);
  const file = useFileSave();
  // which card has its AI variant buttons open; one at a time keeps the list calm
  const [variantsFor, setVariantsFor] = useState<string | null>(null);
  const qtiImport = useMutation({
    mutationFn: (f: File) => importQti(courseId, f),
    onSuccess: (res) => { setQtiMsg(t("items.qti.done", { n: res.imported, skipped: res.skipped.length })); refresh(); },
    onError: (e) => setQtiMsg(String(e)),
  });
  const exportQti = async () => {
    try {
      file.record(await download(`/curriculum${qtiExportPath(courseId)}`, "items-qti21.zip", "zip"));
      setQtiMsg(null);
    } catch {
      file.record(null);
      setQtiMsg(t("items.qti.exportFailed"));
    }
  };
  const los = course.data?.los ?? [];
  const drafts = (items.data ?? []).filter((it) => it.review_status === "draft");
  // Approving in bulk goes by objective, so it is only offered when the list is that scope.
  const bulkScope = !filters.q && !filters.type && !filters.tag && filters.review_status !== "approved";
  const input = "field";

  async function runGeneration(body: Parameters<typeof generateItems>[1]) {
    setGenState({ busy: true, msg: null });
    try {
      const started = await generateItems(courseId, body);
      const g = await awaitGeneration(courseId, started.job_id);
      if (g.status === "failed") setGenState({ busy: false, msg: t("items.gen.failed", { error: g.error ?? "" }) });
      else setGenState({ busy: false, msg: t("items.gen.done", { n: g.items.length, dropped: g.dropped }) });
      refresh();
    } catch (e) {
      setGenState({ busy: false, msg: isOfflineError(e) ? t("plan.needsAi") : t("items.gen.failed", { error: String(e) }) });
    }
  }

  async function save(body: ItemIn) {
    if (editing === "new") await createItem(courseId, body);
    else if (editing) {
      await patchItem(editing.item_id, body);
      if (editing.source === "ai") {
        // the edit diff hook (STORY-009): what the teacher changed in an AI draft
        const before = { stem: editing.stem, options: editing.options, answer_spec: editing.answer_spec, bloom: editing.bloom, tags: editing.tags };
        const after = { stem: body.stem, options: body.options, answer_spec: body.answer_spec, bloom: body.bloom, tags: body.tags };
        void recordEdit("items", editing.item_id, before, after).catch(() => undefined);
      }
    }
    setEditing(null);
    refresh();
  }

  return (
    <TeacherChrome title={t("items.title")} wide course={course.data ? { id: courseId, title: course.data.title } : null} stage="items">
      {/* The filter bar: the filters share one grid, so their fields line up in columns
          instead of each sizing itself to its widest option; the actions sit in their own
          cluster at the end of the row, on the same 48 px step as the fields. */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="grid-fit min-w-0 max-w-[42rem] flex-1" style={{ "--fit-min": "12rem", gap: "0.75rem" } as CSSProperties}>
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("items.search")}
          <input type="search" name="q" autoComplete="off" className={input} value={filters.q ?? ""} onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })} />
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("editor.objective")}
          <select className={input} value={filters.lo_id ?? ""} onChange={(e) => setFilters({ ...filters, lo_id: e.target.value || undefined })}>
            <option value="">{t("items.all")}</option>
            {los.map((lo) => (
              <option key={lo.lo_id} value={lo.lo_id}>{lo.code}</option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("editor.type")}
          <select className={input} value={filters.type ?? ""} onChange={(e) => setFilters({ ...filters, type: e.target.value || undefined })}>
            <option value="">{t("items.all")}</option>
            {["single", "multi", "numeric", "short_text", "ordering", "hotspot"].map((x) => (
              <option key={x} value={x}>{t(`editor.types.${x}`)}</option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("editor.tags")}
          <input className={input} value={filters.tag ?? ""} onChange={(e) => setFilters({ ...filters, tag: e.target.value || undefined })} />
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          {t("items.status")}
          <select className={input} value={filters.review_status ?? ""} onChange={(e) => setFilters({ ...filters, review_status: e.target.value || undefined })}>
            <option value="">{t("items.all")}</option>
            <option value="draft">{t("items.draft")}</option>
            <option value="approved">{t("items.approved")}</option>
          </select>
        </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setEditing("new")}>{t("items.new")}</Button>
          <Button variant="secondary" onClick={() => void exportQti()}>{t("items.qti.export")}</Button>
          <label className="inline-flex min-h-[var(--control-h)] cursor-pointer items-center rounded-[var(--radius-control)] border px-4 text-sm font-medium hover:bg-[color:var(--surface-2)]">
            {qtiImport.isPending ? t("items.qti.importing") : t("items.qti.import")}
            <input type="file" accept=".zip,.xml" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) qtiImport.mutate(f); e.target.value = ""; }} />
          </label>
          {qtiMsg && <p className="text-sm" role="status">{qtiMsg}</p>}
          <SaveNotice result={file.result} />
        </div>
      </div>

      <section className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-dashed p-4" style={{ borderColor: "var(--draft)" }}>
        <div className="flex flex-wrap items-center gap-2">
          <DraftChip />
          <h2 className="font-semibold">{t("items.gen.title")}</h2>
        </div>
        <p className="measure text-sm text-[color:var(--ink-2)]">{t("items.gen.hint")}</p>
        <form
          className="flex flex-wrap items-end gap-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void runGeneration({ lo_id: gen.lo_id, count: gen.count, types: [gen.type], bloom: gen.bloom || null, materialise: true });
          }}
        >
          <label className="flex min-w-0 flex-col gap-1">
            {t("editor.objective")}
            <select className={`${input} max-w-[26rem]`} value={gen.lo_id} onChange={(e) => setGen({ ...gen, lo_id: e.target.value })} required>
              <option value="">—</option>
              {los.map((lo) => (
                <option key={lo.lo_id} value={lo.lo_id}>{lo.code} · {lo.text.length > 60 ? `${lo.text.slice(0, 60)}…` : lo.text}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            {t("editor.type")}
            <select className={input} value={gen.type} onChange={(e) => setGen({ ...gen, type: e.target.value })}>
              {["single", "multi", "numeric", "short_text"].map((x) => (
                <option key={x} value={x}>{t(`editor.types.${x}`)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            {t("editor.bloom")}
            <select className={input} value={gen.bloom} onChange={(e) => setGen({ ...gen, bloom: e.target.value })}>
              <option value="">—</option>
              {["remember", "understand", "apply", "analyse", "evaluate", "create"].map((x) => (
                <option key={x} value={x}>{t(`editor.blooms.${x}`)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            {t("items.gen.count")}
            <input type="number" inputMode="numeric" min={1} max={8} className={`${input} w-20`} value={gen.count} onChange={(e) => setGen({ ...gen, count: Number(e.target.value) || 1 })} />
          </label>
          <Button type="submit" aria-describedby={noAi ? "no-ai" : undefined} disabled={!gen.lo_id || noAi} loading={genState.busy}>{t("items.gen.cta")}</Button>
        </form>
        {noAi && <p id="no-ai" className="text-sm text-[color:var(--ink-2)]">{t("plan.needsAi")}</p>}
        {genState.busy && <p className="text-sm text-[color:var(--ink-2)]" aria-live="polite">{t("items.gen.working")}</p>}
        {genState.msg && <p className="text-sm" role="status">{genState.msg}</p>}
      </section>

      {stats.data && (
        <section className="flex flex-col gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-labelledby="review-queue">
          <h2 id="review-queue" className="font-semibold">{t("items.stats.title")}</h2>
          <p className="measure text-sm text-[color:var(--ink-2)]">{t("items.stats.hint", { n: stats.data.min_n })}</p>
          {queue.length === 0 && <p className="text-sm">{t("items.stats.none")}</p>}
          <ul className="flex flex-col gap-2">
            {queue.map((s) => {
              const it = items.data?.find((x) => x.item_id === s.item_id);
              const rw = rewriteByItem.get(s.item_id);
              const refused = rewriteRefused[s.item_id];
              const state = refused ? "none" : rewriteState(rw);
              return (
                <li key={s.item_id} className="flex flex-wrap items-start justify-between gap-3 rounded-[var(--radius-card)] border p-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2">{s.stem}</p>
                    <p className="text-xs text-[color:var(--ink-2)]">{statLine(s)}</p>
                    <ul className="mt-1 flex flex-wrap gap-1">
                      {s.flags.map((f) => (
                        <li key={f.code} className="rounded-full border px-2 text-xs" style={{ borderColor: "var(--not-yet)" }}>{flagText(f)}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1">
                    <Button variant="tertiary" size="sm" disabled={!it} onClick={() => it && setEditing(it)}>{t("items.stats.edit")}</Button>
                    <Button variant="tertiary" size="sm" loading={review.isPending} onClick={() => review.mutate({ itemId: s.item_id, action: "dismiss" })}>{t("items.stats.dismiss")}</Button>
                    {rewritesHere && state !== "working" && state !== "ready" && (
                      <Button variant="tertiary" size="sm" onClick={() => void propose(s)}>{t("items.rewrite.propose")}</Button>
                    )}
                  </div>
                  {rewritesHere && (
                    <div className="basis-full">
                      <p className="text-sm text-[color:var(--ink-2)]" aria-live="polite">{state === "working" ? t("items.rewrite.working") : ""}</p>
                      {state === "ready" && rw?.draft && (
                        <div className="mt-4">
                        <DraftFrame>
                          <div className="flex flex-col gap-2">
                            <p className="font-medium">{t("items.rewrite.proposal")}</p>
                            <p className="break-words" lang={rw.lang}>{rw.draft.stem}</p>
                            {rw.rationale && <p className="measure break-words text-[color:var(--ink-2)]" lang={rw.lang}>{rw.rationale}</p>}
                            {rw.suspect_key && <p className="font-medium text-[color:var(--danger)]"><span aria-hidden>⚠ </span>{t("items.rewrite.suspectKey")}</p>}
                            <div>
                              <Button variant="secondary" size="sm" onClick={() => void openDraft(rw)}>{t("items.rewrite.open")}</Button>
                            </div>
                          </div>
                        </DraftFrame>
                        </div>
                      )}
                      {(refused || state === "failed") && <p className="text-sm" role="status">{failureText(refused ?? rw?.error)}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {dismissed.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-[color:var(--ink-2)]">{t("items.stats.dismissed")} · {dismissed.length}</summary>
              <ul className="mt-2 flex flex-col gap-1">
                {dismissed.map((s) => (
                  <li key={s.item_id} className="flex items-center justify-between gap-2">
                    <span className="line-clamp-1">{s.stem}</span>
                    <Button variant="tertiary" size="sm" onClick={() => review.mutate({ itemId: s.item_id, action: "reopen" })}>{t("items.stats.reopen")}</Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {editing !== null && (
        <ItemEditor
          item={editing === "new" ? null : editing}
          los={los}
          lang={i18n.language.startsWith("ru") ? "ru" : "en"}
          courseId={courseId}
          aiOff={noAi}
          reviewStatus={editing === "new" ? null : editing.review_status}
          onReview={editing === "new" ? undefined : async (status) => { setEditing(await setItemReview(editing.item_id, status)); refresh(); }}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        {drafts.length > 0 && (
          <div className="flex flex-1 flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-dashed p-3 text-sm" style={{ borderColor: "var(--draft)" }}>
            <DraftChip />
            <p className="flex-1">{t("items.approval.pending", { count: drafts.length })}</p>
            {bulkScope && (
              <Button
                variant="secondary"
                loading={approveDrafts.isPending}
                onClick={async () => {
                  const label = filters.lo_id ? t("items.approval.approveAllOnObjective") : t("items.approval.approveAll");
                  if (await confirm({ title: t("items.approval.approveAllTitle"), body: t("items.approval.approveAllConfirm", { count: drafts.length }), confirmLabel: label })) approveDrafts.mutate();
                }}
              >
                {filters.lo_id ? t("items.approval.approveAllOnObjective") : t("items.approval.approveAll")}
              </Button>
            )}
          </div>
        )}
        {reviewMsg && <p className="text-sm" role="status">{reviewMsg}</p>}
      </div>

      {/* Eight actions used to sit on the same line as the stem and squeeze it into a third
          of the row; they now form their own bar under the text, so the question reads at
          full width, and the cards themselves fill a wide window in columns. */}
      <ul className="grid-fit" style={{ "--fit-min": "30rem" } as CSSProperties}>
        {items.data?.map((it) => (
          <li key={it.item_id} className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3 text-sm shadow-sm">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-2)]">
                <span className="rounded-full border px-2">{t(`editor.types.${it.type}`)}</span>
                {it.lo_id && <span>{los.find((l) => l.lo_id === it.lo_id)?.code ?? ""}</span>}
                {it.bloom && <span>{t(`editor.blooms.${it.bloom}`)}</span>}
                {it.review_status === "draft" ? (
                  <DraftChip kind={it.source === "ai" ? "draft" : "edited"} />
                ) : (
                  <span className="text-[color:var(--ok)]">✓ {t("items.approved")}</span>
                )}
                {(it.tags ?? []).map((tag) => (
                  <span key={tag} className="rounded-full bg-[color:var(--bg)] px-2">#{tag}</span>
                ))}
              </div>
              <p className="line-clamp-2">{it.stem}</p>
              {statById.has(it.item_id) && <p className="mt-1 text-xs text-[color:var(--ink-2)]">{statLine(statById.get(it.item_id)!)}</p>}
              {(it.options ?? []).length > 0 && (
                <p className="mt-1 text-xs text-[color:var(--ink-2)]">
                  {(it.options ?? []).map((o) => `${String(o.key)}${o.correct ? "✓" : ""}`).join(" · ")}
                </p>
              )}
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-x-1 gap-y-2 border-t border-[color:var(--line)] pt-2">
              {it.review_status === "draft" ? (
                <Button variant="secondary" size="sm" loading={setStatus.isPending && setStatus.variables?.itemId === it.item_id} onClick={() => setStatus.mutate({ itemId: it.item_id, status: "approved" })}>
                  {t("items.approval.approve")}
                </Button>
              ) : (
                <Button variant="tertiary" size="sm" loading={setStatus.isPending && setStatus.variables?.itemId === it.item_id} onClick={() => setStatus.mutate({ itemId: it.item_id, status: "draft" })}>
                  {t("items.approval.toDraft")}
                </Button>
              )}
              <Button variant="tertiary" size="sm" onClick={() => setEditing(it)}>{t("items.edit")}</Button>
              <Button variant="tertiary" size="sm" onClick={() => duplicate.mutate(it.item_id)}>{t("items.duplicate")}</Button>
              <Button
                variant="tertiary"
                size="sm"
                onClick={async () => {
                  if (await confirm({ title: t("items.archiveTitle"), body: t("items.archiveConfirm"), confirmLabel: t("items.archive"), destructive: true })) archive.mutate(it.item_id);
                }}
              >
                {t("items.archive")}
              </Button>
              {it.lo_id && !noAi && (
                /* Three generators on every card made a wall of actions. They fold behind one
                   control: the card shows what a teacher does often (approve, edit) and opens
                   the AI variants on request. With no model reachable the control is not
                   rendered at all -- a dead button on every card is worse than none, and the
                   panel above already says drafting needs a server. Draft violet: status only. */
                <span className="flex items-center gap-1 text-xs" role="group" aria-label={t("items.gen.variant")}>
                  <Button
                    variant="tertiary"
                    size="sm"
                    aria-expanded={variantsFor === it.item_id}
                    onClick={() => setVariantsFor(variantsFor === it.item_id ? null : it.item_id)}
                  >
                    {t("items.gen.variantLabel")}
                  </Button>
                  {variantsFor === it.item_id &&
                    (["easier", "harder", "angle"] as const).map((v) => (
                      <Button key={v} variant="tertiary" size="sm" disabled={genState.busy || noAi} onClick={() => void runGeneration({ lo_id: it.lo_id!, count: 1, variant: v, variant_of: it.item_id, materialise: true })}>
                        {t(`items.gen.${v}`)}
                      </Button>
                    ))}
                </span>
              )}
            </div>
          </li>
        ))}
        {items.data && items.data.length === 0 && <li className="text-sm text-[color:var(--ink-2)]">{t("items.none")}</li>}
      </ul>
    </TeacherChrome>
  );
}
